import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Brain, LayoutGrid, Dumbbell, Users2, Lightbulb, Scale, Award, BarChart3, LogOut, Zap, Menu, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, t, roleLabel } from "@/lib/api";
import { LangToggle } from "@/components/Common";

const NAV = () => [
  { to: "/app", label: t("Accueil", "Home"), icon: LayoutGrid, end: true, id: "accueil" },
  { to: "/app/ateliers", label: t("Ateliers guidés", "Guided workshops"), icon: Dumbbell, id: "ateliers" },
  { to: "/app/simulateur", label: t("Simulateur IA", "AI simulator"), icon: Users2, id: "simulateur" },
  { to: "/app/idees", label: t("Labo d'idées", "Idea lab"), icon: Lightbulb, id: "laboratoire" },
  { to: "/app/matrice", label: t("Matrice de décision", "Decision matrix"), icon: Scale, id: "matrice" },
  { to: "/app/profil", label: t("Badges & profil", "Badges & profile"), icon: Award, id: "profil" },
];

export const Brand = () => (
  <div className="flex items-center gap-2.5">
    <div className="relative h-9 w-9 rounded-xl bg-indigo-600 flex items-center justify-center">
      <Brain className="h-5 w-5 text-white" />
      <span className="absolute -top-1 -right-1 h-3.5 w-3.5 rounded-full bg-amber-400 border-2 border-white spark-pulse" />
    </div>
    <span className="font-display text-xl font-bold tracking-tight">SparkMind</span>
  </div>
);

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [points, setPoints] = useState(null);
  const [open, setOpen] = useState(false);
  const isManager = ["manager", "admin"].includes(user.role);

  useEffect(() => {
    const load = () => api.get("/me/profile").then(({ data }) => setPoints(data)).catch(() => {});
    load();
    setOpen(false);
    window.addEventListener("points-updated", load);
    return () => window.removeEventListener("points-updated", load);
  }, [location.pathname]);

  const items = isManager ? [...NAV(), { to: "/app/rh", label: t("Dashboard RH", "HR dashboard"), icon: BarChart3, id: "dashboard-rh" }] : NAV();

  const nav = (
    <nav className="flex flex-col gap-1">
      {items.map(({ to, label, icon: Icon, end, id }) => (
        <NavLink key={to} to={to} end={end} data-testid={`nav-link-${id}`}
          className={({ isActive }) => `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-indigo-600 text-white shadow-md shadow-indigo-200" : "text-slate-600 hover:bg-indigo-50 hover:text-indigo-700"}`}>
          <Icon className="h-4 w-4" /> {label}
        </NavLink>
      ))}
    </nav>
  );

  const side = (
    <div className="flex h-full flex-col gap-8 p-5">
      <div className="hidden lg:block"><Brand /></div>
      {nav}
      <div className="mt-auto space-y-3">
        <LangToggle />
        {points && (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4" data-testid="sidebar-points-widget">
            <div className="flex items-center gap-2 text-amber-700 text-xs font-semibold uppercase tracking-wider"><Zap className="h-3.5 w-3.5" /> {t("Niveau", "Level")} {points.level}</div>
            <div className="font-display text-2xl font-bold text-slate-900 mt-1" data-testid="sidebar-points-value">{points.points} pts</div>
            <div className="mt-2 h-1.5 rounded-full bg-amber-100 overflow-hidden">
              <div className="h-full bg-amber-500" style={{ width: `${((points.points % 150) / 150) * 100}%` }} />
            </div>
          </div>
        )}
        <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-3">
          {user.picture ? <img src={user.picture} alt="" className="h-9 w-9 rounded-full" /> :
            <div className="h-9 w-9 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-semibold">{user.name?.[0]}</div>}
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-semibold" data-testid="sidebar-user-name">{user.name}</div>
            <div className="text-xs text-slate-500 capitalize" data-testid="sidebar-user-role">{roleLabel(user.role)}</div>
          </div>
          <button data-testid="logout-button" onClick={async () => { await logout(); navigate("/"); }} className="rounded-lg p-2 text-slate-500 hover:bg-rose-50 hover:text-rose-600 transition-colors" title={t("Se déconnecter", "Log out")}>
            <LogOut className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen grain">
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 border-r border-slate-200 bg-white/80 backdrop-blur-xl z-30">{side}</aside>
      <header className="lg:hidden sticky top-0 z-40 flex items-center justify-between border-b border-slate-200 bg-white/80 backdrop-blur-xl px-4 py-3">
        <Brand />
        <button data-testid="mobile-menu-toggle" onClick={() => setOpen(!open)} className="p-2">{open ? <X /> : <Menu />}</button>
      </header>
      {open && <div className="lg:hidden fixed inset-0 top-[61px] z-30 bg-white">{side}</div>}
      <main className="relative z-10 lg:pl-64">
        <div className="max-w-[1400px] px-5 sm:px-8 lg:px-12 py-8 lg:py-12"><Outlet /></div>
      </main>
    </div>
  );
}
